// Print report (the "Download PDF" output). A purpose-built, multi-page, black-on-white sheet
// designed as a sales-coach's working document — not just a scorecard — that mirrors the app's
// visual identity (dark masthead, gold accent, Bebas Neue display type) while staying legible in
// print. Skeleton: branded masthead → call details → coach's quick take → outcome → what worked /
// gaps (each with a "Coach:" line) → skill glossary → a single, unsplittable closing card
// (Project Moneyball pitch + signature + QR). Built into a print-only #report node so
// window.print() yields a clean PDF, not the dashboard.
//
// Frontend trust-zone note: all copy here (glossary, PM pitch) is written fresh for this file. It
// must never import from the Worker's scoring/rubric code — Pages ships no prompts or IP.

import type { AggregateResult, CallOwnership, SkillBreakdownRow, BuyerSideResult, WhySignals, MeddpiccReport } from '../lib/types.js';
import { BONUS_LABEL_INLINE, formatBonus } from '../lib/bonusCopy.js';
import { displaySkillName } from '../lib/skills.js';
import { improvementGaps } from './improvement.js';
import { setText } from '../lib/escape.js';

const BUYER_QUAD_LABEL: Record<BuyerSideResult['quadrant'], string> = {
  advancing: 'Advancing',
  warm_stalled: 'Warm · Stalled',
  false_positive: 'False-Positive · Fragile',
  at_risk: 'At-Risk · Going Dark',
};

/** Buyer-Signals block for the report. Buyer Signals is on every plan, so it's always included: the
 *  full read once generated, or a short "not generated for this call" note otherwise. */
function renderBuyerSideReport(buyer: BuyerSideResult | null): HTMLElement {
  const wrap = el('div', 'rep-buyer');
  if (!buyer) {
    wrap.appendChild(el('p', 'rep-blurb', 'Buyer Signals has not been generated for this call.'));
    return wrap;
  }
  wrap.appendChild(el('p', 'rep-blurb', `${BUYER_QUAD_LABEL[buyer.quadrant]}. ${buyer.headline}`));
  const tbl = el('div', 'rep-skills');
  const head = el('div', 'rep-skill-row rep-skill-head');
  head.appendChild(el('span', 'rs-name', 'Buyer signal'));
  head.appendChild(el('span', 'rs-val', 'Score'));
  head.appendChild(el('span', 'rs-val', ''));
  head.appendChild(el('span', 'rs-flag', ''));
  tbl.appendChild(head);
  const rows: Array<[string, number]> = [['Belief', buyer.intent], ['Commitment', buyer.commitment], ['Evidence', buyer.evidence_score]];
  for (const [name, val] of rows) {
    const row = el('div', 'rep-skill-row');
    row.appendChild(el('span', 'rs-name', name));
    row.appendChild(el('span', 'rs-val', `${val} / 100`));
    row.appendChild(el('span', 'rs-val', ''));
    row.appendChild(el('span', 'rs-flag', ''));
    tbl.appendChild(row);
  }
  wrap.appendChild(tbl);
  wrap.appendChild(el('p', 'rep-blurb', `Based on ${buyer.attribution.buyer_statements} buyer statement${buyer.attribution.buyer_statements === 1 ? '' : 's'}. Buyer attribution only, seller words never move the score.`));
  if (buyer.actions.length) wrap.appendChild(bulletList(buyer.actions));
  if (buyer.why_signals) wrap.appendChild(renderWhyReport(buyer.why_signals));
  return wrap;
}

/** The four buying-case questions for the report, paired with their WhySignals key. */
const WHY_REPORT_QUESTIONS: Array<[keyof WhySignals, string]> = [
  ['why_solution', 'Why they seek a solution'],
  ['why_now', 'Why now'],
  ['why_us', 'Why us'],
  ['cost_of_inaction', 'Cost of inaction (CIA)'],
];

/** "Why the buyer is buying" — verbatim buyer quotes for the four questions (quotes only, no scoring).
 *  Each question shows its bullets or a "no evidence found" note so all four stay in the report. */
function renderWhyReport(w: WhySignals): HTMLElement {
  const wrap = el('div', 'rep-why');
  wrap.appendChild(el('div', 'rep-why-title', "Why the buyer is buying (in the buyer's own words)"));
  for (const [key, label] of WHY_REPORT_QUESTIONS) {
    const block = el('div', 'rep-why-q');
    block.appendChild(el('div', 'rep-why-qh', label));
    const quotes = w[key];
    if (quotes.length) block.appendChild(bulletList(quotes.map((q) => `“${q}”`), 'rep-list'));
    else block.appendChild(el('p', 'rep-why-empty', 'No evidence found in this call.'));
    wrap.appendChild(block);
  }
  return wrap;
}

export interface ReportMeta {
  repName: string;
  repRole: string;
  businessName: string;
  callType: string;
  callDuration?: string;
}

const PM_BLURB =
  'Project Moneyball helps B2B SaaS teams turn call-level signals into commercial discipline, ' +
  'reverse-engineering what good looks like across the customer lifecycle and replicating it, ' +
  'so growth becomes repeatable rather than dependent on a few top performers. This report is ' +
  'built to be read in a 1:1, every gap below comes with a concrete coaching move, not just a score.';

const PM_NEXT_STEPS: { title: string; body: string }[] = [
  { title: 'Reinforcement workshops', body: "Turn this session's findings into a repeatable habit across the whole team." },
  { title: '1:1 coaching with Avner Baruch', body: 'Sit down with this exact scorecard and build a focused improvement plan.' },
  { title: 'Books & frameworks', body: "Avner's books lay out the methodology behind this scoring, for the whole team to read." },
  { title: 'Advisory & consulting', body: 'Ongoing GTM guidance so good becomes repeatable, not dependent on a few top performers.' },
];

// Plain-English, manager-facing definitions — independent of (and simpler than) the scoring rubric
// the model uses. Keyed by skill_name, matching what the aggregator already returns.
const SKILL_GLOSSARY: Record<string, string> = {
  'Setting Expectations': 'Did the rep set a clear, mutual agenda, purpose, time, and what a good outcome looks like?',
  'Call Prep': 'Did the rep show up prepared, recapping prior context instead of asking the buyer to repeat themselves?',
  Personalization: "Did the rep tailor the conversation to this buyer's situation, not run a generic pitch?",
  'Problem Development': 'Did the rep dig past the surface ask to uncover the real business driver behind it?',
  'Moneyball Gap Identifier': 'Did the rep establish a clear, buyer-validated gap between today and the desired future state, tied to value?',
  'A-Ha Moment': 'Did the buyer visibly shift their thinking during the call, a real realization, not polite agreement?',
  'Objection Handling': 'When pushback came up, did the rep address it directly and credibly?',
  Competitive: 'Did the rep proactively shape how the buyer thinks about alternatives, competitors, build vs. buy, or doing nothing?',
  'Domain Expertise': "Did the rep speak the buyer's language and command their market and challenges convincingly?",
  'Active Listening': 'Did the rep react to what the buyer just said, especially anything personal or emotional, rather than continuing a scripted flow?',
  'Next Steps': "Did the call end with a clear, mutually committed next step, not a vague 'I'll follow up'?",
};

/** Score tier → CSS class (mirrors the dashboard circle: bad <70 / capped, mid <100, good 100+). */
function scoreTier(final: number, capped?: boolean): string {
  if (capped || final < 70) return 'rep-bad';
  if (final < 100) return 'rep-mid';
  return 'rep-good';
}

function el(tag: string, className?: string, text?: string): HTMLElement {
  const n = document.createElement(tag);
  if (className) n.className = className;
  if (text != null) setText(n, text);
  return n;
}

function bulletList(items: string[], cls = 'rep-list'): HTMLElement {
  const ul = el('ul', cls);
  const list = items.length ? items : [', '];
  for (const it of list) {
    const li = document.createElement('li');
    setText(li, it);
    ul.appendChild(li);
  }
  return ul;
}

function section(title: string, body: HTMLElement, modifier?: string): HTMLElement {
  const wrap = el('div', modifier ? `rep-section ${modifier}` : 'rep-section');
  wrap.appendChild(el('h2', 'rep-h', title));
  wrap.appendChild(body);
  return wrap;
}

/** Deduplicate next-steps across skills (same logic as the dashboard Recommendations box). */
function dedupedRecommendations(rows: SkillBreakdownRow[]): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const s of rows) {
    for (const rec of s.recommendations ?? []) {
      const key = rec.toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();
      if (!key || seen.has(key)) continue;
      seen.add(key);
      out.push(rec);
    }
  }
  return out;
}

function joinNames(names: string[]): string {
  if (names.length === 0) return '';
  if (names.length === 1) return names[0]!;
  if (names.length === 2) return `${names[0]} and ${names[1]}`;
  return `${names.slice(0, -1).join(', ')}, and ${names[names.length - 1]}`;
}

/**
 * A deterministic, code-composed "read this before the 1:1" line — no model call, so it's as
 * reproducible as the scores it summarizes. Picks the biggest shortfalls and the strongest,
 * bar-clearing skills so a manager has a starting point in ten seconds, not ten minutes.
 */
function coachQuickTake(rows: SkillBreakdownRow[]): string {
  const gapNames = improvementGaps(rows)
    .filter((s) => s.actual_score !== null)
    .sort((a, b) => b.expected_score - b.actual_score! - (a.expected_score - a.actual_score!))
    .slice(0, 2)
    .map((s) => displaySkillName(s.skill_name));
  const strengthNames = rows
    .filter((s) => s.attainment !== null && s.attainment >= 100 && s.actual_score !== null)
    .sort((a, b) => b.actual_score! - a.actual_score!)
    .slice(0, 2)
    .map((s) => displaySkillName(s.skill_name));

  if (gapNames.length && strengthNames.length) {
    return (
      `Coach this session around ${joinNames(gapNames)}, that's where the call fell short of the bar. ` +
      `${joinNames(strengthNames)} carried it and are worth reinforcing as the model to repeat.`
    );
  }
  if (gapNames.length) {
    return `Coach this session around ${joinNames(gapNames)}, that's where the call fell short of the bar.`;
  }
  if (strengthNames.length) {
    return `This call cleared the bar across the board, ${joinNames(strengthNames)} stood out and are worth calling out to the rep by name.`;
  }
  return 'Review the full breakdown below with the rep, no single skill dominates the story on this call.';
}

/** "Who was scored — and why" — methodology + this call's RACI determination (multi-rep calls). */
function renderOwnership(o: CallOwnership): HTMLElement {
  const wrap = el('div', 'rep-ownership');
  wrap.appendChild(
    el(
      'p',
      'rep-blurb',
      'More than one rep from your side was on this call. STUDIO attributes the scorecard to the ' +
        'call owner, the rep responsible for driving the conversation (RACI R/A): the one who controls the ' +
        'agenda, fields the buyer’s questions, owns positioning and proof, and closes. Supporting reps are noted ' +
        'but do not carry the score, and the buyer is never scored. This keeps the score about one person’s execution. ' +
        'RACI key: R/A = drives the call (scored); C = a supporting voice; I = present but passive.',
    ),
  );
  const scored = el('p', 'rep-blurb');
  const strong = document.createElement('strong');
  setText(strong, o.primary_rep ? `Scored: ${o.primary_rep}  ·  ${o.confidence} confidence` : 'Primary rep: shared / unclear');
  scored.appendChild(strong);
  wrap.appendChild(scored);

  const ul = el('ul', 'rep-list');
  for (const p of o.participants) {
    const li = document.createElement('li');
    setText(li, `${p.name}, ${p.role} (${p.raci})${p.note ? `: ${p.note}` : ''}`);
    ul.appendChild(li);
  }
  for (const b of o.buyer_side) {
    const role = o.buyer_side_roles?.find((x) => x.name === b);
    const li = document.createElement('li');
    setText(li, role ? `${b}, Buyer (${role.raci}), not scored` : `${b}, Buyer (not scored)`);
    ul.appendChild(li);
  }
  wrap.appendChild(ul);

  if (o.signals.length) {
    wrap.appendChild(el('p', 'rep-blurb', 'Why this rep owns the call:'));
    wrap.appendChild(bulletList(o.signals));
  }
  return wrap;
}

/** One "worked" or "gap" block: name + score line, summary, and a skill-specific coaching move —
 * the thing a manager actually reads out loud in a 1:1, not just a bullet to skim past. */
function skillBlock(s: SkillBreakdownRow, cls: string, coachLabel: string): HTMLElement {
  const wrap = el('div', `rep-gap ${cls}`);
  const row = el('div', 'rep-gap-row');
  row.appendChild(el('span', 'rep-gap-name', displaySkillName(s.skill_name)));
  const scored = s.actual_score !== null;
  row.appendChild(
    el('span', 'rep-gap-score', scored ? `${s.actual_score!.toFixed(1)} / ${s.expected_score.toFixed(1)} min` : 'no evidence'),
  );
  wrap.appendChild(row);
  if (s.summary) wrap.appendChild(el('div', 'rep-gap-summary', s.summary));
  const rec = s.recommendations?.[0];
  if (rec) {
    const coach = el('div', 'rep-gap-coach');
    const label = document.createElement('strong');
    setText(label, `${coachLabel}: `);
    coach.appendChild(label);
    coach.appendChild(document.createTextNode(rec));
    wrap.appendChild(coach);
  }
  return wrap;
}

function skillBlocks(rows: SkillBreakdownRow[], cls: string, coachLabel: string, emptyMsg: string): HTMLElement {
  const wrap = el('div', 'rep-gaplist');
  if (!rows.length) {
    wrap.appendChild(el('p', 'rep-blurb', emptyMsg));
    return wrap;
  }
  for (const s of rows) wrap.appendChild(skillBlock(s, cls, coachLabel));
  return wrap;
}

/** Cross-Skill Diagnostic — the connected-skills narrative (the same high-level summary shown on the
 * app's Cross-Skill Diagnostic page), rendered as running paragraphs so the PDF carries the "how the
 * skills lifted or dragged each other" story, not just the per-skill scorecard. */
function renderCrossSkillDiagnostic(paragraphs: string[]): HTMLElement {
  const wrap = el('div', 'rep-crossskill');
  for (const para of paragraphs) wrap.appendChild(el('p', 'rep-blurb', para));
  return wrap;
}

/** Repeating page footer (print). A single fixed element the browser paints on every printed page:
 * the SCORE wordmark + site URL. */
export function renderFooter(): HTMLElement {
  const footer = el('div', 'rep-footer');
  footer.appendChild(el('span', 'rep-footer-mark', 'STUDIO'));
  footer.appendChild(el('span', 'rep-footer-sep', '|'));
  const link = document.createElement('a');
  link.className = 'rep-footer-link';
  link.href = 'https://www.studio-moneyball.io';
  setText(link, 'www.studio-moneyball.io');
  footer.appendChild(link);
  return footer;
}

function renderGlossary(rows: SkillBreakdownRow[]): HTMLElement {
  const grid = el('div', 'rep-glossary');
  for (const s of rows) {
    const def = SKILL_GLOSSARY[s.skill_name];
    if (!def) continue;
    const item = el('div', 'rep-glossary-item');
    item.appendChild(el('div', 'rep-glossary-name', displaySkillName(s.skill_name)));
    item.appendChild(el('div', 'rep-glossary-def', def));
    grid.appendChild(item);
  }
  return grid;
}

/** The report's single, unsplittable closing block: PM pitch + next steps + signature + QR — the
 * "signature" the report is built around, so break-inside:avoid here is the whole point. */
export function renderClose(): HTMLElement {
  const close = el('div', 'rep-close');

  const main = el('div', 'rep-close-main');
  main.appendChild(el('div', 'rep-h', 'Make this repeatable, not a one-off'));
  main.appendChild(el('p', 'rep-blurb', PM_BLURB));
  const cards = el('div', 'rep-cta-cards');
  for (const step of PM_NEXT_STEPS) {
    const card = el('div', 'rep-cta-card');
    card.appendChild(el('div', 'rep-cta-title', step.title));
    card.appendChild(el('div', 'rep-cta-body', step.body));
    cards.appendChild(card);
  }
  main.appendChild(cards);
  close.appendChild(main);

  const sign = el('div', 'rep-close-sign');
  sign.appendChild(el('div', 'rep-signname', 'Avner Baruch'));
  sign.appendChild(el('div', 'rep-signrole', 'Founder, Project Moneyball'));
  const contact = document.createElement('a');
  contact.className = 'rep-contact';
  contact.href = 'mailto:info@projectmoneyball.com';
  setText(contact, 'info@projectmoneyball.com');
  sign.appendChild(contact);

  const qrBlock = el('div', 'rep-qrblock');
  const qr = document.createElement('img');
  qr.className = 'rep-qr';
  qr.src = '/assets/qr.svg';
  qr.alt = 'Scan for more';
  qrBlock.appendChild(qr);
  qrBlock.appendChild(el('div', 'rep-cta-caption', 'Scan to book time or explore reinforcement options'));
  sign.appendChild(qrBlock);
  close.appendChild(sign);

  return close;
}

/** Ensures every <img> in the report (logo, QR) is fully decoded before window.print() is called.
 * Without this, print/PDF output can silently omit an image that hasn't finished loading yet — the
 * QR code is the report's only first-use of that asset (the logo is usually already cached from
 * elsewhere in the app), so it was the one most likely to render blank. */
export async function waitForReportImages(root: HTMLElement): Promise<void> {
  const imgs = Array.from(root.querySelectorAll('img'));
  await Promise.all(
    imgs.map((img) => {
      if (typeof img.decode === 'function') return img.decode().catch(() => undefined);
      if (img.complete) return Promise.resolve();
      return new Promise<void>((resolve) => {
        img.addEventListener('load', () => resolve(), { once: true });
        img.addEventListener('error', () => resolve(), { once: true });
      });
    }),
  );
}

/** The report's dark branded masthead (logo + title + date). Shared with the Cohort Diagnostic PDF so
 *  both print outputs carry the same header. */
export function reportMasthead(title: string): HTMLElement {
  const masthead = el('div', 'rep-masthead');
  const brand = el('div', 'rep-mast-brand');
  const studioLogo = document.createElement('img');
  studioLogo.className = 'rep-mast-logo';
  studioLogo.src = '/assets/logos/studio-moneyball.svg';
  studioLogo.alt = 'Studio Moneyball';
  brand.appendChild(studioLogo);
  brand.appendChild(el('div', 'rep-mast-title', title));
  masthead.appendChild(brand);
  const mastRight = el('div', 'rep-mast-right');
  const date = new Date().toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' });
  mastRight.appendChild(el('div', 'rep-mast-date', date));
  mastRight.appendChild(el('div', 'rep-mast-tag', 'www.studio-moneyball.io'));
  masthead.appendChild(mastRight);
  return masthead;
}

const MEDD_STATUS_LABEL: Record<string, string> = { strong: 'Strong', weak: 'Weak', missing: 'Missing' };

/** MEDDPICC qualification block for the report — the same non-scoring qualification read shown on the
 *  MEDDPICC page, so the PDF carries it too. Shows a short "not generated" note if it's absent. */
function renderMeddpiccReport(m: MeddpiccReport | null): HTMLElement {
  const wrap = el('div', 'rep-medd');
  if (!m || !m.elements?.length) {
    wrap.appendChild(el('p', 'rep-blurb', 'MEDDPICC qualification has not been generated for this call.'));
    return wrap;
  }
  wrap.appendChild(el('p', 'rep-blurb', 'A qualification read (non-scoring): how well the deal is qualified across the eight MEDDPICC elements.'));
  for (const e of m.elements) {
    const item = el('div', 'rep-medd-item');
    const head = el('div', 'rep-medd-head');
    head.appendChild(el('span', 'rep-medd-letter', e.letter));
    head.appendChild(el('span', 'rep-medd-label', e.label));
    head.appendChild(el('span', `rep-medd-status rep-medd-status-${e.status}`, MEDD_STATUS_LABEL[e.status] ?? e.status));
    item.appendChild(head);
    if (e.note) item.appendChild(el('div', 'rep-medd-note', e.note));
    if (e.evidence) item.appendChild(el('div', 'rep-medd-ev', `“${e.evidence}”`));
    wrap.appendChild(item);
  }
  return wrap;
}

export function renderReport(root: HTMLElement, r: AggregateResult, meta: ReportMeta, buyer: BuyerSideResult | null = null, meddpicc: MeddpiccReport | null = null): void {
  root.innerHTML = '';
  const sheet = el('div', 'rep-page');

  // --- Masthead: dark branded band (Studio Moneyball logo, report title, date) ---
  sheet.appendChild(reportMasthead('Coaching Report'));

  // --- Call details strip (mirrors the dashboard's Call Details panel) ---
  const details: { label: string; value: string }[] = [
    { label: 'Rep', value: meta.repName + (meta.repRole ? ` (${meta.repRole})` : '') },
    { label: 'Account', value: meta.businessName },
    { label: 'Call type', value: meta.callType },
    { label: 'Duration', value: meta.callDuration ?? '' },
  ].filter((d) => d.value.trim().length > 0);
  if (details.length) {
    const strip = el('div', 'rep-details');
    for (const d of details) {
      const field = el('div', 'rep-details-field');
      field.appendChild(el('div', 'rep-details-label', d.label));
      field.appendChild(el('div', 'rep-details-value', d.value));
      strip.appendChild(field);
    }
    sheet.appendChild(strip);
  }

  // --- Coach's Quick Take: a ten-second, code-composed synthesis before the tables ---
  sheet.appendChild(section('Coach’s quick take', el('p', 'rep-blurb rep-quicktake', coachQuickTake(r.skill_breakdown))));

  // --- Outcome: score + coverage + per-skill Actual vs Expected ---
  const outcome = el('div', 'rep-outcome');
  const scoreCol = el('div', `rep-scorecol ${scoreTier(r.final_score, r.capped)}`);
  scoreCol.appendChild(el('div', 'rep-score', String(r.final_score)));
  scoreCol.appendChild(el('div', 'rep-meaning', r.meaning_label));
  // Coverage/confidence and the optional-skill bonus are separate facts — the bonus comes from skills
  // marked optional, not from having good evidence. Kept on distinct lines so print says so too.
  scoreCol.appendChild(
    el('div', 'rep-coverage', `${Math.round(r.evidence_coverage * 100)}% scorecard coverage · ${r.confidence_label} confidence`),
  );
  // Always printed, "None" at zero — the PDF is the archived record, so an absent line there would be
  // ambiguous long after the call.
  scoreCol.appendChild(
    el('div', 'rep-coverage rep-bonus', `${BONUS_LABEL_INLINE} ${formatBonus(r.optional_bonus ?? 0)}`),
  );
  outcome.appendChild(scoreCol);

  const tbl = el('div', 'rep-skills');
  const head = el('div', 'rep-skill-row rep-skill-head');
  head.appendChild(el('span', 'rs-name', 'Skill'));
  head.appendChild(el('span', 'rs-val', 'Actual'));
  head.appendChild(el('span', 'rs-val', 'Desired State (min)'));
  head.appendChild(el('span', 'rs-flag', ''));
  tbl.appendChild(head);
  for (const s of r.skill_breakdown) {
    const scored = s.actual_score !== null;
    const meets = s.attainment !== null && s.attainment >= 100;
    const row = el('div', 'rep-skill-row');
    row.appendChild(el('span', 'rs-name', displaySkillName(s.skill_name)));
    row.appendChild(el('span', 'rs-val', scored ? s.actual_score!.toFixed(1) : ', '));
    row.appendChild(el('span', 'rs-val', s.expected_score ? s.expected_score.toFixed(1) : 'opt'));
    // ✓ (green) = meets the manager minimum; ▼ (red, points DOWN) = below the minimum.
    const flagCls = scored ? (meets ? 'rs-flag rs-meets' : 'rs-flag rs-gap') : 'rs-flag';
    row.appendChild(el('span', flagCls, scored ? (meets ? '✓' : '▼') : ''));
    tbl.appendChild(row);
  }
  outcome.appendChild(tbl);
  sheet.appendChild(section('Outcome', outcome));

  // --- Worked / Gaps, each skill with its own coaching line — the report's actual coaching payload ---
  const worked = r.skill_breakdown.filter((s) => s.attainment !== null && s.attainment >= 100);
  // Same shortfall-aware gaps as the on-screen report (improvement.ts) so a scored miss can never be
  // dropped from the PDF even if attainment came back null/rounded.
  const gaps = improvementGaps(r.skill_breakdown);
  sheet.appendChild(section('What worked well', skillBlocks(worked, 'rep-gap-good', 'Reinforce', 'No standout strengths on this call.')));
  sheet.appendChild(section('Areas requiring improvement', skillBlocks(gaps, 'rep-gap-bad', 'Coach', 'None. Every scored skill met or beat its target on this call.')));
  sheet.appendChild(section('All recommendations (next steps)', bulletList(dedupedRecommendations(r.skill_breakdown))));

  // --- Who was scored & why (only when 2+ vendor reps were on the call) ---
  if (r.call_ownership?.multi_rep) {
    sheet.appendChild(section('Who was scored, and why', renderOwnership(r.call_ownership)));
  }

  // --- Cross-Skill Diagnostic — the connected-skills narrative, mirrored from the app's page ---
  const crossParas = r.high_level_summary?.paragraphs ?? [];
  if (crossParas.length) {
    sheet.appendChild(section('Cross-Skill Diagnostic', renderCrossSkillDiagnostic(crossParas), 'rep-section--flow'));
  }

  // --- Buyer Signals — the other half of the call (belief / commitment / evidence + momentum) ---
  sheet.appendChild(section('Buyer Signals', renderBuyerSideReport(buyer), 'rep-section--flow'));

  // --- MEDDPICC qualification — the non-scoring qualification read (its own page in-app) ---
  sheet.appendChild(section('MEDDPICC qualification', renderMeddpiccReport(meddpicc), 'rep-section--flow'));

  // --- Skill glossary — lets a manager coach confidently without memorizing 11 definitions ---
  sheet.appendChild(section('What each skill measures', renderGlossary(r.skill_breakdown), 'rep-section--flow'));

  // --- Closing: PM pitch + next steps + signature + QR, as a single block that cannot be split ---
  sheet.appendChild(renderClose());

  root.appendChild(sheet);
  // Repeating page footer — fixed element the browser reprints on every page (see .rep-footer CSS).
  root.appendChild(renderFooter());
}
